import { useEffect, useMemo, useState } from "react";
import { useTonConnectUI, useTonAddress } from "@tonconnect/ui-react";
import { Address, beginCell, toNano } from "@ton/core";
import {
  STAKING_REWARDS_OPS,
  buildJettonTransferBody,
  buildIncreasePayload,
  buildLockPayload,
} from "@tonado/core";
import { STAKING_ADDRESS, TONADO_MASTER_ADDRESS } from "../lib/env";
import {
  getStakingLockBounds,
  getStakingAccount,
  getJettonWalletViaRelayer,
} from "../lib/relayerRpc";

type Action = "lock" | "increase" | "extend" | "claim" | "withdraw";

interface LockInfo {
  amount: bigint;
  lockEnd: number;
  votingPower: bigint;
  claimable: bigint;
}

/**
 * veTONADO staking surface. Lock TONADO, top up, extend, claim rewards,
 * or withdraw the principal after lockEnd. Reads global pool state +
 * the connected wallet's lock from the staking contract.
 */
export function VeTonado() {
  const [tonConnectUI] = useTonConnectUI();
  const sender = useTonAddress();
  const connected = Boolean(sender);

  const [stakingAddr, setStakingAddr] = useState(STAKING_ADDRESS);
  const [tonadoMaster, setTonadoMaster] = useState(TONADO_MASTER_ADDRESS);
  const [action, setAction] = useState<Action>("lock");
  const [amount, setAmount] = useState("");
  const [durationDays, setDurationDays] = useState("30");
  const [extendDays, setExtendDays] = useState("30");
  const [bounds, setBounds] = useState<{ min: number; max: number } | null>(null);
  const [lock, setLock] = useState<LockInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const stakingValid = useMemo(() => safeAddress(stakingAddr), [stakingAddr]);
  const masterValid = useMemo(() => safeAddress(tonadoMaster), [tonadoMaster]);

  // Read staking pool config + (if connected) the user's lock state.
  useEffect(() => {
    if (!stakingValid) {
      setBounds(null);
      setLock(null);
      return;
    }
    (async () => {
      try {
        // All chain reads go through the relayer's server-side toncenter
        // — the dApp ships no API key. Lock bounds are global; account
        // state is per-user, so we only fetch it when a wallet is
        // connected.
        const { minLockSeconds, maxLockSeconds } = await getStakingLockBounds(
          stakingValid.toString()
        );
        setBounds({ min: minLockSeconds, max: maxLockSeconds });
        if (sender) {
          const account = await getStakingAccount(stakingValid.toString(), sender);
          if (!account.isLocked) {
            setLock(null);
            return;
          }
          setLock({
            amount: BigInt(account.amount),
            lockEnd: account.lockEnd,
            votingPower: BigInt(account.votingPower),
            claimable: BigInt(account.claimable),
          });
        }
      } catch (err) {
        setError(`Read failed: ${(err as Error).message}`);
      }
    })();
  }, [stakingValid, sender]);

  async function handleSubmit() {
    setError(null);
    setInfo(null);
    if (!stakingValid) {
      setError("Set veTONADO contract address first");
      return;
    }
    if (!connected) {
      setError("Connect wallet first");
      return;
    }
    setBusy(true);
    try {
      // Jetton-routed ops: lock + increase. Send TEP-74 transfer to the
      // staking contract with a payload-flagged forward_payload. Resolve
      // the user's TONADO wallet via the relayer (server-side toncenter)
      // since the dApp ships no API key.
      if (action === "lock" || action === "increase") {
        if (!masterValid) throw new Error("Set TONADO master address");
        const base = parseAmountToBaseUnits(amount);
        if (base === 0n) throw new Error("Amount must be > 0");
        const userJettonWalletStr = await getJettonWalletViaRelayer(masterValid.toString(), sender);
        const userJettonWallet = Address.parse(userJettonWalletStr);
        const payload =
          action === "lock"
            ? buildLockPayload(Number(durationDays) * 86400)
            : buildIncreasePayload();
        const transferBody = buildJettonTransferBody({
          queryId: 0n,
          amount: base,
          destination: stakingValid,
          responseDestination: Address.parse(sender),
          forwardTonAmount: toNano("0.1"),
          forwardPayload: payload,
        });
        await tonConnectUI.sendTransaction({
          validUntil: Math.floor(Date.now() / 1000) + 360,
          messages: [
            {
              address: userJettonWallet.toString(),
              amount: toNano("0.25").toString(),
              payload: transferBody.toBoc().toString("base64"),
            },
          ],
        });
      } else {
        // Direct ops: extend, claim, withdraw. Send a tiny TON transfer
        // straight to the staking contract.
        let body;
        let value: bigint;
        if (action === "extend") {
          const seconds = Number(extendDays) * 86400;
          if (seconds <= 0) throw new Error("Extend duration must be > 0");
          body = beginCell()
            .storeUint(STAKING_REWARDS_OPS.EXTEND_LOCK, 32)
            .storeUint(0n, 64)
            .storeUint(seconds, 32)
            .endCell();
          value = toNano("0.05");
        } else if (action === "claim") {
          body = beginCell()
            .storeUint(STAKING_REWARDS_OPS.CLAIM_REWARDS, 32)
            .storeUint(0n, 64)
            .endCell();
          value = toNano("0.2");
        } else {
          body = beginCell()
            .storeUint(STAKING_REWARDS_OPS.WITHDRAW, 32)
            .storeUint(0n, 64)
            .endCell();
          value = toNano("0.25");
        }
        await tonConnectUI.sendTransaction({
          validUntil: Math.floor(Date.now() / 1000) + 360,
          messages: [
            {
              address: stakingValid.toString(),
              amount: value.toString(),
              payload: body.toBoc().toString("base64"),
            },
          ],
        });
      }
      setInfo("Transaction sent. Refresh the page in a few seconds to see updated state.");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flow flow--ve">
      <h2 className="flow__title">veTONADO staking</h2>
      <p className="lede">
        Lock TONADO for governance voting power and a share of buyback revenue. Voting power =
        amount × lockDuration / maxLockSeconds.
      </p>

      <fieldset className="summary summary--inputs">
        <label className="input-row">
          <span>veTONADO address</span>
          <input
            type="text"
            value={stakingAddr}
            onChange={(e) => setStakingAddr(e.target.value)}
            placeholder="EQ..."
            className="mono mono--break"
          />
        </label>
        {(action === "lock" || action === "increase") && (
          <label className="input-row">
            <span>TONADO master</span>
            <input
              type="text"
              value={tonadoMaster}
              onChange={(e) => setTonadoMaster(e.target.value)}
              placeholder="EQ..."
              className="mono mono--break"
            />
          </label>
        )}
      </fieldset>

      {bounds && (
        <p className="lede lede--small">
          Lock bounds: {(bounds.min / 86400).toFixed(0)}d - {(bounds.max / 86400).toFixed(0)}d.
        </p>
      )}

      {lock && (
        <div className="summary">
          <SummaryRow label="Your lock" value={`${formatTonado(lock.amount)} TONADO`} />
          <SummaryRow
            label="Lock ends"
            value={new Date(lock.lockEnd * 1000).toISOString().slice(0, 19) + "Z"}
          />
          <SummaryRow label="Voting power" value={lock.votingPower.toString()} mono />
          <SummaryRow label="Claimable" value={`${formatTonado(lock.claimable)} TONADO`} accent />
        </div>
      )}

      <div className="tabs tabs--inline" role="tablist">
        {(["lock", "increase", "extend", "claim", "withdraw"] as Action[]).map((a) => (
          <button
            key={a}
            type="button"
            role="tab"
            aria-selected={action === a}
            className={`tabs__item ${action === a ? "tabs__item--active" : ""}`}
            onClick={() => setAction(a)}
          >
            <span className="tabs__label">{a.charAt(0).toUpperCase() + a.slice(1)}</span>
          </button>
        ))}
      </div>

      {(action === "lock" || action === "increase") && (
        <label className="input-row">
          <span>Amount (TONADO)</span>
          <input
            type="text"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="100"
          />
        </label>
      )}
      {action === "lock" && (
        <label className="input-row">
          <span>Duration (days)</span>
          <input
            type="text"
            value={durationDays}
            onChange={(e) => setDurationDays(e.target.value)}
            placeholder="30"
          />
        </label>
      )}
      {action === "extend" && (
        <label className="input-row">
          <span>Extend by (days)</span>
          <input
            type="text"
            value={extendDays}
            onChange={(e) => setExtendDays(e.target.value)}
            placeholder="30"
          />
        </label>
      )}

      <div className="cta-row">
        <button
          type="button"
          className="btn btn--primary btn--lg"
          disabled={busy || !connected || !stakingValid}
          onClick={handleSubmit}
        >
          {busy
            ? "Submitting…"
            : !connected
              ? "Connect wallet"
              : action === "lock"
                ? "Lock TONADO"
                : action === "increase"
                  ? "Add to lock"
                  : action === "extend"
                    ? "Extend lock"
                    : action === "claim"
                      ? "Claim rewards"
                      : "Withdraw principal"}
        </button>
      </div>

      {error && (
        <p className="error">
          <strong>Error:</strong> {error}
        </p>
      )}
      {info && <p className="success__kicker">{info}</p>}
    </div>
  );
}

function safeAddress(s: string): Address | null {
  try {
    return Address.parse(s);
  } catch {
    return null;
  }
}

function parseAmountToBaseUnits(amount: string): bigint {
  if (!amount.trim()) return 0n;
  const [intPart, fracPart = ""] = amount.split(".");
  const padded = (fracPart + "000000000").slice(0, 9);
  return BigInt(intPart || "0") * 10n ** 9n + BigInt(padded || "0");
}

function formatTonado(base: bigint): string {
  const whole = base / 10n ** 9n;
  const frac = (base % 10n ** 9n).toString().padStart(9, "0").replace(/0+$/, "");
  return frac ? `${whole}.${frac}` : whole.toString();
}

function SummaryRow({
  label,
  value,
  accent,
  mono,
}: {
  label: string;
  value: string;
  accent?: boolean;
  mono?: boolean;
}) {
  return (
    <div className={`summary__row ${accent ? "summary__row--accent" : ""}`}>
      <dt>{label}</dt>
      <dd className={mono ? "mono mono--break" : ""}>{value}</dd>
    </div>
  );
}
